import { observer } from 'mobx-react-lite'
import { type UIEvent, useCallback, useEffect, useRef } from 'react'
import {
  EnumSpinnerLoaderPlacement,
  type IScrollbarsPropsRef,
  Scrollbar,
  SpinnerLoader,
  Typography,
} from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useResultsContext } from 'pages/conversations/pages/search/ui/Results/context/resultsContext'
import { MessageCard } from 'pages/conversations/pages/search/ui/Results/ui/MessageCard/MessageCard'
import { logV2SearchEvent } from 'pages/conversations/pages/search/utils/logSearchEvent'
import styles from './styles.module.scss'

export const SectionAllMessages = observer(() => {
  const entities = useEntitiesContext()
  const { resultsMessagesStore, handleGoToProfile } = useResultsContext()
  const store = resultsMessagesStore
  const scrollRef = useRef<IScrollbarsPropsRef | null>(null)

  const handleScroll = useCallback((e: UIEvent) => {
    const target = e.target as HTMLDivElement
    const scrollHeight = target.scrollHeight
    const scrollTop = target.scrollTop
    const offsetHeight = target.offsetHeight
    const indentBottom = 20

    if (store.loading) return
    if (store.loadingMore) return
    if (!store.hasMore) return
    if (scrollHeight <= scrollTop + offsetHeight + indentBottom) {
      store.loadMessages({ more: true })
    }
  }, [])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollToTop()
    }
  }, [])

  if (store.loading) {
    return (
      <div className={styles.loading}>
        <SpinnerLoader
          ariaLabel={'SectionAllMessages'}
          size={20}
          placement={EnumSpinnerLoaderPlacement.Top}
        />
      </div>
    )
  }

  if (!store.itemsAll.length) {
    return (
      <Typography
        tag={'div'}
        variant={'body-sm-regular'}
        ariaLabel={'empty'}
        className={styles.empty}
      >
        No messages found
      </Typography>
    )
  }

  return (
    <div className={styles.wrap}>
      <Scrollbar ref={scrollRef} onScroll={handleScroll} indentX={20}>
        {store.itemsAll.map((item) => {
          return (
            <MessageCard
              key={item.id}
              item={item}
              onActionClick={(item) => {
                handleGoToProfile(item)

                logV2SearchEvent(
                  'Search Page Result Clicked',
                  {
                    event_id: 'search_page_result_clicked',
                    SearchResultClickedType: 'Message_separate_tab',
                  },
                  entities
                )
              }}
            />
          )
        })}
        {store.loadingMore && (
          <div className={styles.loadingMore}>
            <SpinnerLoader ariaLabel={'SectionAllMessages'} size={20} />
          </div>
        )}
      </Scrollbar>
    </div>
  )
})
